import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useNavigate, useSearchParams } from 'react-router';
import styled from 'styled-components';
import { Button } from '../components/Button';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { ShareLinkService } from '../services/sharelink/ShareLinkService';
/**
 * Screen-share deep link for AI agents (and quick human use): /share
 * ?surface=screen|window|tab&name=<displayName>&title=<sessionTitle>.
 *
 * One click on the start button captures the chosen surface and goes live
 * at /stream/<id> — the address-bar URL there is the shareable viewer link.
 * The click is mandatory: getDisplayMedia requires a user gesture.
 *
 * Automation contract (also used by the agent skill and e2e tests):
 * - [data-automation-id="start-share"] — the start/retry button
 * - [data-share-state="idle|capturing|starting|error"] — on the page root
 * - [data-automation-id="share-error"] — error text when state is 'error'
 */
export function SharePage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const sharePhase = ShareLinkService.getOutput().useSharePhase();
    const errorMessage = ShareLinkService.getOutput().useErrorMessage();
    const surface = searchParams.get('surface');
    const surfaceLabel = surface === 'window'
        ? 'a window'
        : surface === 'tab'
            ? 'a tab'
            : 'the screen';
    const onStartClick = () => {
        ShareLinkService.getNotificationInterface().notifyStartShare({
            surface,
            name: searchParams.get('name'),
            title: searchParams.get('title'),
        }, (path) => void navigate(path));
    };
    return (_jsxs(SharePageRoot, { "data-share-state": sharePhase, children: [_jsx(LoadingIndicator, { loading: sharePhase === 'starting' }), _jsxs(Card, { children: [_jsxs(Title, { children: ["Share ", surfaceLabel] }), sharePhase === 'starting' ? (_jsx(Description, { children: "Going live\u2026" })) : sharePhase === 'capturing' ? (_jsx(Description, { children: "Waiting for capture approval \u2014 choose what to share in the browser dialog." })) : (_jsxs(Description, { children: ["Starts a live broadcast of ", surfaceLabel, ". The page address after starting is the link viewers use to watch."] })), errorMessage !== undefined && (_jsx(ErrorText, { "data-automation-id": "share-error", children: errorMessage })), sharePhase !== 'starting' && (_jsx(Button, { "data-automation-id": "start-share", type: "button", disabled: sharePhase === 'capturing', onClick: onStartClick, children: sharePhase === 'error' ? 'Try again' : 'Start sharing' }))] })] }));
}
const SharePageRoot = styled.div `
    position: fixed;
    inset: 0;
    background-color: #000000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
`;
const Card = styled.div `
    max-width: 26rem;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    text-align: center;
`;
const Title = styled.h1 `
    margin: 0;
    color: #ffffff;
    font-size: 1.375rem;
    font-weight: 500;
    line-height: 1.75rem;
`;
const Description = styled.p `
    margin: 0;
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.9375rem;
    line-height: 1.375rem;
`;
const ErrorText = styled.p `
    margin: 0;
    color: #ff8a80;
    font-size: 0.9375rem;
    line-height: 1.375rem;
`;
